// src/pages/SubmitPage.js
// Public page — no auth required. Third party submits files or credentials.

import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { formatBytes, formatDate } from '../utils/api';
import Icon from '../components/Icon';

const BASE = '/api';
async function apiCall(path, options = {}) {
  const res = await fetch(`${BASE}/${path}`, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
}

function OTPBoxes({ onComplete }) {
  const [digits, setDigits] = useState(Array(6).fill(''));
  const refs = useRef([]);
  const onChange = (i, val) => {
    const d = val.replace(/\D/g, '').slice(-1);
    const next = [...digits]; next[i] = d; setDigits(next);
    if (d && i < 5) refs.current[i + 1]?.focus();
    if (next.every(x => x)) onComplete(next.join(''));
  };
  const onKeyDown = (i, e) => { if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i - 1]?.focus(); };
  const onPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) { setDigits(pasted.split('')); onComplete(pasted); e.preventDefault(); }
  };
  return (
    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', margin: '20px 0' }}>
      {digits.map((d, i) => (
        <input key={i} ref={el => refs.current[i] = el} type="text" inputMode="numeric" maxLength={1} value={d}
          onChange={e => onChange(i, e.target.value)} onKeyDown={e => onKeyDown(i, e)} onPaste={onPaste}
          style={{ width: '44px', height: '52px', textAlign: 'center', fontSize: '22px', fontWeight: 700, border: '2px solid var(--g300)', outline: 'none', fontFamily: 'monospace' }} />
      ))}
    </div>
  );
}

const Header = () => (
  <div className="receive-header">
    <div className="receive-crest"><Icon size={36} /></div>
    <div style={{ lineHeight: 1.2 }}>
      <div className="receive-brand">SUFC SECURE TRANSFER SYSTEM</div>
    </div>
  </div>
);

export default function SubmitPage() {
  const { token } = useParams();
  const [stage, setStage] = useState('loading'); // loading | error | otp-request | verify-otp | submit | done | expired
  const [requestInfo, setRequestInfo] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [session, setSession] = useState(null);
  const [otpError, setOtpError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [files, setFiles] = useState([]);
  const [credContent, setCredContent] = useState('');
  const [progress, setProgress] = useState({});
  const [submitBusy, setSubmitBusy] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');
  const fileInputRef = useRef();

  useEffect(() => {
    apiCall(`submit/request/${token}`)
      .then(d => { setRequestInfo(d); setStage('otp-request'); })
      .catch(e => {
        if (e.message.includes('already been used') || e.message.includes('expired')) setStage('expired');
        else setStage('error');
      });
  }, [token]);

  const requestOTP = async () => {
    setOtpError('');
    try {
      const d = await apiCall(`submit/otp-request/${token}`, { method: 'POST', body: '{}' });
      setMaskedEmail(d.maskedEmail);
      setStage('verify-otp');
    } catch (e) { setOtpError(e.message); }
  };

  const verifyOTP = async (code) => {
    setVerifying(true); setOtpError('');
    try {
      const d = await apiCall(`submit/otp-verify/${token}`, { method: 'POST', body: JSON.stringify({ code }) });
      setSession(d);
      setStage('submit');
    } catch (e) { setOtpError(e.message); setVerifying(false); }
  };

  const handleFileUploadAndSubmit = async () => {
    if (!files.length) return;
    setSubmitBusy('uploading');
    try {
      // Prepare
      const prepared = await apiCall(`submit/upload-prepare/${token}`, {
        method: 'POST',
        headers: { 'x-session-token': session.sessionToken },
        body: JSON.stringify({ fileNames: files.map(f => f.name), fileSizes: files.map(f => f.size) }),
      });
      // Upload
      for (const file of files) {
        const sasUrl = prepared.uploadUrls[file.name];
        if (!sasUrl) throw new Error(`No URL for ${file.name}`);
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', sasUrl);
          xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
          xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
          xhr.upload.addEventListener('progress', e => {
            if (e.lengthComputable) setProgress(p => ({ ...p, [file.name]: Math.round(e.loaded / e.total * 100) }));
          });
          xhr.onload = () => xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
          xhr.onerror = () => reject(new Error('Network error — check CORS settings on storage account'));
          xhr.send(file);
        });
      }
      // Confirm
      setSubmitBusy('confirming');
      await apiCall(`submit/upload-confirm/${token}`, {
        method: 'POST',
        headers: { 'x-session-token': session.sessionToken },
        body: JSON.stringify({ fileNames: files.map(f => f.name), fileSizes: files.map(f => f.size) }),
      });
      setStage('done');
    } catch (e) { setSubmitStatus(`❌ ${e.message}`); }
    finally { setSubmitBusy(''); }
  };

  const handleCredentialSubmit = async () => {
    if (!credContent.trim()) return;
    setSubmitBusy('submitting');
    try {
      await apiCall(`submit/credential/${token}`, {
        method: 'POST',
        headers: { 'x-session-token': session.sessionToken },
        body: JSON.stringify({ content: credContent.trim() }),
      });
      setStage('done');
    } catch (e) { setSubmitStatus(`❌ ${e.message}`); }
    finally { setSubmitBusy(''); }
  };

  if (stage === 'loading') return (
    <div className="receive-page"><div className="receive-card"><Header />
      <div className="receive-body" style={{ textAlign: 'center', padding: '40px' }}>
        <div className="spinner" style={{ margin: '0 auto 16px' }} /><p>Loading…</p>
      </div>
    </div></div>
  );

  if (stage === 'expired' || stage === 'error') return (
    <div className="receive-page"><div className="receive-card"><Header />
      <div className="receive-body" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>{stage === 'expired' ? '🔗' : '⚠️'}</div>
        <h2 className="receive-title">{stage === 'expired' ? 'Link expired or already used' : 'Link not found'}</h2>
        <p className="receive-from">{stage === 'expired' ? 'This link has already been used or has expired. Please contact the sender for a new link.' : 'This link is invalid. Please check you have the correct URL.'}</p>
      </div>
    </div></div>
  );

  if (stage === 'done') return (
    <div className="receive-page"><div className="receive-card"><Header />
      <div className="receive-body" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '56px', marginBottom: '16px' }}>✅</div>
        <h2 className="receive-title">{requestInfo?.requestType === 'credential' ? 'Credentials submitted' : 'Files uploaded'}</h2>
        <p className="receive-from">{requestInfo?.requesterName} has been notified. This link has now been used and cannot be used again.</p>
        <p style={{ fontSize: '12px', color: 'var(--g400)', marginTop: '16px' }}>🔒 Your submission has been encrypted and logged securely.</p>
      </div>
    </div></div>
  );

  if (stage === 'otp-request') return (
    <div className="receive-page"><div className="receive-card"><Header />
      <div className="receive-body">
        <h2 className="receive-title">{requestInfo?.requestType === 'credential' ? '🔐 Secure credential submission' : '📥 Secure file upload'}</h2>
        <p className="receive-from"><strong>{requestInfo?.requesterName}</strong> has requested {requestInfo?.requestType === 'credential' ? 'credentials' : 'files'} from you.</p>
        {requestInfo?.message && (
          <div style={{ background: 'var(--g50)', border: '1px solid var(--g200)', padding: '12px 16px', marginBottom: '16px', fontSize: '14px', color: 'var(--g700)' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--g400)', marginBottom: '6px' }}>Message</div>
            {requestInfo.message}
          </div>
        )}
        <div style={{ background: 'var(--g50)', border: '1px solid var(--g200)', padding: '12px 16px', marginBottom: '20px', fontSize: '13px' }}>
          <div style={{ marginBottom: '4px' }}><strong>Request:</strong> {requestInfo?.title}</div>
          <div><strong>Expires:</strong> {formatDate(requestInfo?.expiresAt)}</div>
        </div>
        <div className="alert alert-warn" style={{ marginBottom: '20px' }}>
          ⚠️ <strong>This link is single-use.</strong> Once you submit, it cannot be used again. Make sure you are ready before proceeding.
        </div>
        {otpError && <div className="alert alert-error" style={{ marginBottom: '12px' }}>❌ {otpError}</div>}
        <button className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }} onClick={requestOTP}>
          Send verification code to my email
        </button>
        <p style={{ fontSize: '12px', color: 'var(--g400)', marginTop: '12px', textAlign: 'center' }}>We'll send a one-time code to verify your identity before you can submit.</p>
      </div>
    </div></div>
  );

  if (stage === 'verify-otp') return (
    <div className="receive-page"><div className="receive-card"><Header />
      <div className="receive-body">
        <h2 className="receive-title">Check your email</h2>
        <p className="receive-from">We sent a 6-digit code to {maskedEmail}</p>
        {otpError && <div className="alert alert-error">❌ {otpError}</div>}
        {verifying ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }} /><p>Verifying…</p>
          </div>
        ) : (
          <OTPBoxes onComplete={verifyOTP} />
        )}
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <button style={{ background: 'none', border: 'none', color: 'var(--g500)', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }}
            onClick={() => { setOtpError(''); requestOTP(); }}>Resend code</button>
        </div>
      </div>
    </div></div>
  );

  if (stage === 'submit') {
    if (requestInfo?.requestType === 'credential') return (
      <div className="receive-page"><div className="receive-card"><Header />
        <div className="receive-body">
          <h2 className="receive-title">✅ Identity verified</h2>
          <p className="receive-from">Enter the credentials requested by <strong>{requestInfo.requesterName}</strong></p>
          {requestInfo.message && (
            <div style={{ background: 'var(--g50)', border: '1px solid var(--g200)', padding: '12px', marginBottom: '16px', fontSize: '13px', color: 'var(--g700)' }}>"{requestInfo.message}"</div>
          )}
          <textarea className="form-textarea" rows={8} placeholder="Paste passwords, API keys, tokens or any sensitive text here…"
            value={credContent} onChange={e => setCredContent(e.target.value)}
            style={{ fontFamily: 'var(--font-mono)', fontSize: '13px', resize: 'vertical', width: '100%', marginBottom: '12px' }} />
          <div className="alert alert-warn" style={{ marginBottom: '16px' }}>
            ⚠️ Once submitted this link cannot be used again. The information will be encrypted and only accessible to {requestInfo.requesterName}.
          </div>
          {submitStatus && <div className="alert alert-error" style={{ marginBottom: '12px' }}>{submitStatus}</div>}
          <button className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }}
            onClick={handleCredentialSubmit} disabled={!!submitBusy || !credContent.trim()}>
            {submitBusy ? 'Submitting…' : '🔐 Submit credentials'}
          </button>
          <p style={{ fontSize: '12px', color: 'var(--g400)', marginTop: '12px', textAlign: 'center' }}>🔒 Encrypted in transit and at rest</p>
        </div>
      </div></div>
    );

    // File upload
    return (
      <div className="receive-page"><div className="receive-card"><Header />
        <div className="receive-body">
          <h2 className="receive-title">✅ Identity verified</h2>
          <p className="receive-from">Upload the files requested by <strong>{requestInfo.requesterName}</strong></p>
          {requestInfo.message && (
            <div style={{ background: 'var(--g50)', border: '1px solid var(--g200)', padding: '12px', marginBottom: '16px', fontSize: '13px', color: 'var(--g700)' }}>"{requestInfo.message}"</div>
          )}
          <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }}
            onChange={e => setFiles(f => [...f, ...Array.from(e.target.files || [])])} />
          <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center', marginBottom: '12px' }}
            onClick={() => fileInputRef.current.click()} disabled={!!submitBusy}>
            📂 Choose files
          </button>
          {files.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              {files.map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: '1px solid var(--g100)' }}>
                  <span>📄</span>
                  <span style={{ flex: 1, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                  <span style={{ fontSize: '12px', color: 'var(--g400)', whiteSpace: 'nowrap' }}>{formatBytes(f.size)}</span>
                  {submitBusy === 'uploading' && progress[f.name] !== undefined && (
                    <div style={{ width: '60px', height: '4px', background: 'var(--g200)' }}>
                      <div style={{ width: `${progress[f.name]}%`, height: '100%', background: 'var(--green)' }} />
                    </div>
                  )}
                  {!submitBusy && (
                    <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: '16px', fontWeight: 700 }}
                      onClick={() => setFiles(fs => fs.filter((_, j) => j !== i))}>×</button>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="alert alert-warn" style={{ marginBottom: '16px' }}>
            ⚠️ Once uploaded this link cannot be used again.
          </div>
          {submitStatus && <div className="alert alert-error" style={{ marginBottom: '12px' }}>{submitStatus}</div>}
          <button className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }}
            onClick={handleFileUploadAndSubmit} disabled={!!submitBusy || !files.length}>
            {submitBusy === 'uploading' ? 'Uploading…' : submitBusy === 'confirming' ? 'Confirming…' : `⬆️ Upload ${files.length || ''} file${files.length !== 1 ? 's' : ''}`}
          </button>
          <p style={{ fontSize: '12px', color: 'var(--g400)', marginTop: '12px', textAlign: 'center' }}>🔒 Files are encrypted in transit and at rest</p>
        </div>
      </div></div>
    );
  }

  return null;
}
